import { useEffect, useState } from 'react';
import {
  PERMISSION_MODULES,
  updateRolePermissions,
  type RoleRecord,
} from '@/services/users';
import type { Permissions, PermissionLevel } from '@/lib/rbac';

interface PermissionMatrixProps {
  role: RoleRecord;
  onSaved: () => void;
}

const LEVELS: Array<{ key: keyof PermissionLevel; label: string }> = [
  { key: 'read', label: 'Leer' },
  { key: 'write', label: 'Escribir' },
  { key: 'delete', label: 'Eliminar' },
];

export default function PermissionMatrix({ role, onSaved }: PermissionMatrixProps) {
  const [draft, setDraft] = useState<Permissions>(role.permissions);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setDraft(role.permissions);
    setError('');
    setSuccess(false);
  }, [role]);

  function toggle(moduleKey: string, level: keyof PermissionLevel) {
    setDraft((prev) => {
      const current: PermissionLevel = prev[moduleKey] ?? { read: false, write: false, delete: false };
      const next = { ...current, [level]: !current[level] };
      // Cascada: sin "leer" no puede haber "escribir"/"eliminar".
      if (level === 'read' && !next.read) {
        next.write = false;
        next.delete = false;
      }
      // Si habilita "escribir"/"eliminar", habilita también "leer".
      if (level !== 'read' && next[level]) {
        next.read = true;
      }
      return { ...prev, [moduleKey]: next };
    });
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    setSuccess(false);
    try {
      await updateRolePermissions(role.id, draft);
      setSuccess(true);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron guardar los permisos.');
    } finally {
      setSaving(false);
    }
  }

  const grantedCount = PERMISSION_MODULES.filter((m) => draft[m.key]?.read).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-foreground-900">
            {grantedCount} de {PERMISSION_MODULES.length} módulos con acceso de lectura
          </p>
          <p className="text-xs text-foreground-500">
            Los cambios aplican de inmediato a todos los usuarios con este rol.
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="text-sm px-5 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-60 whitespace-nowrap font-medium cursor-pointer"
        >
          {saving ? 'Guardando...' : 'Guardar permisos'}
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2.5">
          <i className="ri-error-warning-line mt-0.5"></i>
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-start gap-2 text-sm bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg px-3 py-2.5">
          <i className="ri-check-line mt-0.5"></i>
          <span>Permisos actualizados correctamente.</span>
        </div>
      )}

      <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-100 text-left bg-background-50">
                <th className="px-5 py-3 text-xs font-medium text-foreground-500">Módulo</th>
                {LEVELS.map((l) => (
                  <th key={l.key} className="px-5 py-3 text-xs font-medium text-foreground-500 text-center">
                    {l.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PERMISSION_MODULES.map((m) => {
                const perm: PermissionLevel = draft[m.key] ?? { read: false, write: false, delete: false };
                return (
                  <tr key={m.key} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                    <td className="px-5 py-2.5 text-foreground-800">{m.label}</td>
                    {LEVELS.map((l) => (
                      <td key={l.key} className="px-5 py-2.5 text-center">
                        <button
                          type="button"
                          onClick={() => toggle(m.key, l.key)}
                          className={`w-5 h-5 rounded border flex items-center justify-center transition-colors cursor-pointer ${
                            perm[l.key]
                              ? 'bg-accent-500 border-accent-500 text-white'
                              : 'bg-white border-background-300 text-transparent hover:border-accent-400'
                          }`}
                          aria-label={`${l.label} en ${m.label}`}
                        >
                          <i className="ri-check-line text-xs"></i>
                        </button>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}